import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Fade, IconButton, Snackbar } from '@material-ui/core';
import {
  Announcement, ArrowBack, Description, Star,
} from '@material-ui/icons';
import {
  compose, filter, first, get, includes, isEmpty, isNull, join, map, range, size, uniqBy,
} from 'lodash/fp';
import dayjs from 'dayjs';
import qs from 'query-string';
import ContentLoader from 'react-content-loader';

import { RetryDialog, Spinner } from '../../../components';
import { closeWebView, isWebView } from '../../../utils';
import * as newsletterActions from '../../newsletter/actions';
import * as actions from '../actions';
import { ChildrenFilter, NewsletterFilter } from '../components';

const S = {
  Wrapper: styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
  `,
  ActionBar: styled.header`
    display: flex;
    align-items: center;
    min-height: 56px;
    padding: 0 12px;
    border-bottom: 1px solid ${({ theme }) => theme.palette.grey[200]};
    background-color: ${({ theme }) => theme.palette.primary.contrastText};
    ${({ seamless }) => seamless && css`
      border-bottom: none;
    `}
  `,
  Heading: styled.h2`
    margin: 0 auto 0 32px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: 1.25rem;
    font-weight: 500;
    line-height: 1.2;
  `,
  Scrollable: styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  `,
  ArrowBack: styled(ArrowBack)`
    color: ${({ theme }) => theme.palette.grey[600]};
  `,
  Padder: styled.div`
    padding: 8px 16px;
  `,
  NewsletterList: styled.ul`
    margin-bottom: 1px;
  `,
  NewsletterItem: styled.li`
    position: relative;
    :not(:first-child) {
      border-top: 1px solid ${({ theme }) => theme.palette.grey[200]};
    }
  `,
  Link: styled(Link)`
    display: block;
    width: 100%;
    height: 100%;
    padding: 16px;
  `,
  NewsletterTitle: styled.h3`
    max-width: 80%;
    margin-bottom: 0.125rem;
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    font-weight: 500;
    line-height: 1.53;
    color: ${({ theme }) => theme.palette.grey[900]};
  `,
  TargetInfo: styled.p`
    margin-bottom: 0.75rem;
    font-size: 0.75rem;
    line-height: 1.5;
    color: ${({ theme }) => theme.palette.grey[500]};
  `,
  SendAt: styled.time`
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    line-height: 1.54;
    color: ${({ theme }) => theme.palette.grey[500]};
  `,
  IconButton: styled(IconButton)`
    position: absolute;
    top: ${({ theme }) => theme.typography.pxToRem(13)};
    right: ${({ theme }) => theme.typography.pxToRem(13)};
  `,
  Star: styled(({ active, ...otherProps }) => <Star {...otherProps} />)`
    color: ${({ theme }) => theme.palette.grey[400]};
    font-size: 1.25rem;
    ${({ active, theme }) => active && css`
      color: ${theme.palette.secondary.main};
    `}
  `,
  Status: styled.p`
    position: absolute;
    bottom: 1rem;
    right: 1rem;
    color: ${({ active, recorded, theme }) => (active ? get(recorded ? 'info' : 'error.main', theme.palette.status) : theme.palette.grey[600])};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    line-height: 1.54;
  `,
  EmptyBox: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
  `,
  Centered: styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 140px;
  `,
  DimmedAnnouncement: styled(Announcement)`
    margin-bottom: 0.75rem;
    color: ${({ theme }) => theme.palette.grey[400]};
    font-size: 3rem;
  `,
  DimmedDescription: styled(Description)`
    margin-bottom: 0.75rem;
    color: ${({ theme }) => theme.palette.grey[400]};
    font-size: 3rem;
  `,
  DimmedStar: styled(Star)`
    margin-bottom: 0.75rem;
    color: ${({ theme }) => theme.palette.grey[400]};
    font-size: 3rem;
  `,
  Paragraph: styled.p`
    margin-bottom: 2rem;
    font-size: 1rem;
    line-height: 1.5;
    color: ${({ theme }) => theme.palette.grey[600]};
  `,
  ClearFilterButton: styled.button`
    color: ${({ theme }) => theme.palette.secondary.main};
    font-size: 1rem;
    line-height: 1.5;
    text-decoration: underline;
    cursor: pointer;
  `,
};

const Newsletters = ({ location }) => {
  const [child, setChild] = useState(0);
  const [type, setType] = useState('all');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const newsletters = useSelector((state) => state.newsletters.newsletters, shallowEqual);
  const favorites = useSelector((state) => state.newsletters.favorites, shallowEqual);
  const favoritesUpdated = useSelector((state) => state.newsletters.favoritesUpdated, shallowEqual);
  const response = useSelector((state) => state.newsletter.response, shallowEqual);

  const targets = useMemo(() => compose(
    map((newsletter) => ({
      school: newsletter.schoolName,
      ...newsletter.target,
    })),
    uniqBy((newsletter) => newsletter.target.gcid),
  )(newsletters.data), [newsletters.data]);
  const hasMultiChild = useMemo(() => size(targets) > 1, [targets]);
  const filteredNewsletters = useMemo(() => (
    filter((newsletter) => ({
      all: true,
      unrecorded: !newsletter.target.recorded,
      favorites: includes(`${newsletter.target.gcid}_${newsletter.id}`, favorites.data),
    }[type]), child === 0
      ? newsletters.data
      : filter((newsletter) => newsletter.target.gcid === child, newsletters.data))
  ), [child, favorites.data, newsletters.data, type]);

  const dispatch = useDispatch();

  const clearFilter = useCallback(() => setType('all'), []);
  const fetchNewsletters = useCallback(() => dispatch(actions.fetchNewslettersRequest()), [dispatch]);
  const fetchFavorites = useCallback(() => dispatch(actions.fetchFavoritesRequest()), [dispatch]);
  const goBack = useCallback(() => {
    if (isWebView()) {
      closeWebView();
    }
  }, []);
  const updateFavorites = useCallback((event) => dispatch(actions.updateFavoritesRequest({
    value: event.currentTarget.dataset.value,
  })), [dispatch]);
  const updateErrorDialog = useCallback(({ entity, update, clear }) => {
    if (entity.error) {
      update(true);

      dispatch(clear());
    }
  }, [dispatch]);

  const handleSnackbarClose = () => setSnackbarOpen(false);
  const handleSnackbarExited = () => setSnackbarMessage('');
  const handleDialogClose = () => setDialogOpen(false);

  useEffect(() => {
    fetchNewsletters();
    fetchFavorites();
  }, [fetchFavorites, fetchNewsletters]);

  useEffect(() => {
    if (response.data) {
      setSnackbarMessage('가정통신문을 제출하였습니다.');

      setSnackbarOpen(true);

      dispatch(newsletterActions.initResponse());
    }
  }, [dispatch, response.data, snackbarOpen]);

  useEffect(() => {
    updateErrorDialog({
      entity: newsletters,
      update: () => setDialogOpen(true),
      clear: actions.initNewsletters,
    });
  }, [newsletters, updateErrorDialog]);

  useEffect(() => {
    updateErrorDialog({
      entity: favorites,
      update: () => {
        setSnackbarMessage('즐겨찾기 정보를 불러오는데 실패하였습니다.');

        setSnackbarOpen(true);
      },
      clear: actions.initFavorites,
    });
  }, [favorites, updateErrorDialog]);

  useEffect(() => {
    updateErrorDialog({
      entity: favoritesUpdated,
      update: () => {
        setSnackbarMessage('즐겨찾기 추가에 실패하였습니다. 잠시 후 다시 시도해주세요.');

        setSnackbarOpen(true);
      },
      clear: actions.initFavoritesUpdated,
    });
  }, [favoritesUpdated, updateErrorDialog]);

  return (
    <>
      <S.Wrapper>
        <S.ActionBar seamless={hasMultiChild}>
          <IconButton type="button" size="small" onClick={goBack}>
            <S.ArrowBack />
          </IconButton>
          <S.Heading>가정통신문</S.Heading>
          <NewsletterFilter active={type !== 'all'} onSelect={setType} />
        </S.ActionBar>
        <S.Scrollable>
          {hasMultiChild && (
            <S.Padder>
              <ChildrenFilter options={targets} value={child} onSelect={setChild} />
            </S.Padder>
          )}
          {newsletters.loading || favorites.loading
            ? map((num) => (
              <ContentLoader
                key={num}
                speed={2}
                primarycolor="#f3f3f3"
                secondarycolor="#ecebeb"
                viewBox="0 0 360 106"
                preserveAspectRatio="none"
              >
                <rect x="16" y="16" rx="3" ry="3" width="292" height="16" />
                <rect x="16" y="42" rx="3" ry="3" width="240" height="16" />
                <rect x="16" y="72" rx="3" ry="3" width="104" height="16" />
                <rect x="308" y="72" rx="3" ry="3" width="36" height="16" />
              </ContentLoader>
            ), range(0, 5))
            : !isEmpty(filteredNewsletters) && (
              <S.NewsletterList>
                {map((newsletter) => (
                  <S.NewsletterItem key={`${newsletter.target.gcid}_${newsletter.id}`}>
                    <S.Link
                      to={{
                        pathname: `/schools/${newsletter.school}/school-newsletters/${newsletter.id}`,
                        state: { from: location.pathname },
                        search: qs.stringify({ gcid: newsletter.target.gcid }),
                      }}
                    >
                      <S.NewsletterTitle>{newsletter.title}</S.NewsletterTitle>
                      <S.TargetInfo>
                        {join('', [
                          newsletter.schoolName ? `${newsletter.schoolName} ` : '',
                          newsletter.target.clazzGroup
                            ? `${newsletter.target.clazzGroup.grade}학년 ${newsletter.target.clazzGroup.clazzName}반 ${newsletter.target.name}`
                            : `${get('isUnspecifiedGroup', first(newsletter.target.customGroups))
                              ? '미지정' : get('name', first(newsletter.target.customGroups))} ${newsletter.target.name}`,
                        ])}
                      </S.TargetInfo>
                      <S.SendAt dateTime={dayjs(get('sendAt', newsletter)).format('YYYY.MM.DD. HH:mm')}>
                        {dayjs(get('sendAt', newsletter)).format('YYYY.MM.DD. HH:mm')}
                      </S.SendAt>
                      <S.Status active={newsletter.status === 'ongoing'} recorded={newsletter.target.recorded}>
                        {newsletter.status === 'ongoing'
                          ? {
                            true: '응답완료',
                            false: '미응답',
                          }[newsletter.target.recorded]
                          : '응답마감'}
                      </S.Status>
                    </S.Link>
                    <S.IconButton
                      type="button"
                      size="small"
                      data-value={`${newsletter.target.gcid}_${newsletter.id}`}
                      onClick={updateFavorites}
                    >
                      <S.Star active={includes(`${newsletter.target.gcid}_${newsletter.id}`, favorites.data)} />
                    </S.IconButton>
                  </S.NewsletterItem>
                ), filteredNewsletters)}
              </S.NewsletterList>
            )}
          {!isNull(newsletters.data) && isEmpty(filteredNewsletters) && ({
            all: type === 'all' && (
              <S.EmptyBox>
                <S.Centered>
                  <S.DimmedDescription />
                  <S.Paragraph>
                    받은 가정통신문이 없습니다.
                  </S.Paragraph>
                </S.Centered>
              </S.EmptyBox>
            ),
            unrecorded: type === 'unrecorded' && (
              <S.EmptyBox>
                <S.Centered>
                  <S.DimmedAnnouncement />
                  <S.Paragraph>
                    미응답한 가정통신문이 없습니다.
                  </S.Paragraph>
                  <S.ClearFilterButton type="button" onClick={clearFilter}>
                    필터 해제
                  </S.ClearFilterButton>
                </S.Centered>
              </S.EmptyBox>
            ),
            favorites: type === 'favorites' && (
              <S.EmptyBox>
                <S.Centered>
                  <S.DimmedStar />
                  <S.Paragraph>
                    즐겨찾기한 가정통신문이 없습니다.
                  </S.Paragraph>
                  <S.ClearFilterButton type="button" onClick={clearFilter}>
                    필터 해제
                  </S.ClearFilterButton>
                </S.Centered>
              </S.EmptyBox>
            ),
          }[type])}
        </S.Scrollable>
      </S.Wrapper>
      {favoritesUpdated.loading && <Spinner />}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3500}
        TransitionComponent={Fade}
        ContentProps={{
          'aria-describedby': 'snackbarMessage',
        }}
        onClose={handleSnackbarClose}
        onExited={handleSnackbarExited}
        message={(
          <span id="snackbarMessage">
            {snackbarMessage}
          </span>
        )}
      />
      <RetryDialog open={dialogOpen} onClose={handleDialogClose} onRetry={fetchNewsletters}>
        목록을 불러오는데 실패하였습니다.
        <br />
        다시 시도하시겠습니까?
      </RetryDialog>
    </>
  );
};

Newsletters.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
};

export default Newsletters;
